import {
  Share2, ZoomIn, FileDown, Users, Briefcase, UserPlus, FilePlus2,
  Clock, Timer, Hourglass, CheckCircle2, type LucideIcon,
} from 'lucide-react';
import { useMetrics, type Metrics } from '@/api/hooks';
import { DashboardBanner } from '@/components/DashboardBanner';

const OUTCOMES = [
  { icon: Share2, title: 'ACCELERATE', caption: 'client & engagement setup' },
  { icon: ZoomIn, title: 'GAIN', caption: 'visibility into requests & status' },
  { icon: FileDown, title: 'ACCESS', caption: 'client & engagement details' },
  { icon: Users, title: 'ENABLE', caption: 'transparency for teams & admins' },
] satisfies { icon: LucideIcon; title: string; caption: string }[];

function fmtDuration(days: number | null, hours: number | null): string {
  if (days == null || hours == null) return '—';
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'}`;
  return `${hours} hr${hours === 1 ? '' : 's'}`;
}

interface Kpi {
  icon: LucideIcon;
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}

// Responsive tile: compact by default so 5 fit per row and Home fits an ~800px
// viewport (150% display scale), and stepped up at 2xl so it looks full-size on a
// large monitor rather than small.
function KpiCard({ icon: Icon, label, value, sub }: Kpi) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-border bg-background/40 p-2.5 2xl:gap-3 2xl:p-3.5">
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent 2xl:h-11 2xl:w-11"
      >
        <Icon className="h-4 w-4 2xl:h-5 2xl:w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-muted-foreground 2xl:text-[11px]">
          {label}
        </div>
        <div className="text-lg font-bold leading-tight text-foreground 2xl:text-2xl">{value}</div>
        {sub ? <div className="truncate text-[10px] text-muted-foreground 2xl:text-[11px]">{sub}</div> : null}
      </div>
    </div>
  );
}

function MetricsSection({ m, loading }: { m?: Metrics; loading: boolean }) {
  const n = (x?: number) => (loading || x == null ? '—' : x.toLocaleString());
  const cards: Kpi[] = [
    { icon: Users, label: 'Total Clients', value: n(m?.totalClients) },
    { icon: Briefcase, label: 'Total Engagements', value: n(m?.totalEngagements) },
    { icon: UserPlus, label: 'Clients via App', value: n(m?.clientsCreatedViaApp), sub: 'new client requests' },
    { icon: FilePlus2, label: 'Engagements via App', value: n(m?.engagementsCreatedViaApp), sub: 'new engagement requests' },
    { icon: Clock, label: 'Avg. Client Completion', value: loading ? '—' : fmtDuration(m?.avgClientCompletionDays ?? null, m?.avgClientCompletionHours ?? null), sub: 'request → completed', accent: true },
    { icon: Timer, label: 'Avg. Eng. Completion', value: loading ? '—' : fmtDuration(m?.avgEngagementCompletionDays ?? null, m?.avgEngagementCompletionHours ?? null), sub: 'request → completed', accent: true },
    { icon: Hourglass, label: 'Pending Clients', value: n(m?.pendingClients) },
    { icon: Hourglass, label: 'Pending Engagements', value: n(m?.pendingEngagements) },
    { icon: CheckCircle2, label: 'Completed Clients', value: n(m?.completedClients) },
    { icon: CheckCircle2, label: 'Completed Engagements', value: n(m?.completedEngagements) },
  ];
  return (
    <div className="rounded-lg bg-card p-4 shadow-sm 2xl:p-5">
      <h2 className="mb-2.5 text-sm font-semibold uppercase tracking-wide text-primary 2xl:mb-4">At a glance</h2>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:gap-3">
        {cards.map((c) => (
          <KpiCard key={c.label} {...c} />
        ))}
      </div>
    </div>
  );
}

export function Home() {
  const { data: metrics, isFetching } = useMetrics();
  return (
    <div className="w-full space-y-3 p-4 2xl:space-y-4 2xl:p-6">
      {/* SS11: the page title now lives in the header bar, so the big in-page
          "Client Administration Dashboard" heading was removed. */}
      <DashboardBanner />
      <MetricsSection m={metrics} loading={isFetching && !metrics} />

      <div className="rounded-lg bg-card px-6 py-3 text-center shadow-sm 2xl:py-5">
        <p className="text-sm font-bold uppercase tracking-wide text-foreground 2xl:text-base">
          One place for new clients, engagements, and changes.
        </p>
        <p className="mx-auto mt-1 max-w-3xl text-xs leading-relaxed text-foreground 2xl:mt-2 2xl:text-sm">
          This new Dashboard makes it easy to manage new clients, engagements, and changes.
          By replacing emails and forms with one centralized process, it saves time,
          reduces errors, and provides fast access to client and engagement information.
        </p>
      </div>

      <div className="rounded-lg bg-secondary/40 px-6 py-4 2xl:py-6">
        <h2 className="mb-3 text-center text-lg font-light uppercase tracking-widest text-foreground/80 2xl:mb-5 2xl:text-2xl">Outcomes</h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {OUTCOMES.map((o) => {
            const Icon = o.icon;
            return (
              <div key={o.title} className="flex items-center justify-center gap-3 text-left">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground shadow 2xl:h-14 2xl:w-14">
                  <Icon className="h-5 w-5 2xl:h-6 2xl:w-6" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-extrabold text-foreground 2xl:text-base">{o.title}</div>
                  <div className="text-xs text-muted-foreground 2xl:text-sm">{o.caption}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
